import React, { createContext, useState, useEffect, useContext, useCallback } from "react"; // Added useCallback
import axios from "axios";
import { useMsal, useIsAuthenticated } from "@azure/msal-react"; // Import MSAL hooks
import { msalConfig, loginRequest } from "../auth/msalConfig"; // CORRECTED PATH

export const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const tokenKey = "isco_jwt";
  const [token, setToken] = useState(() => localStorage.getItem(tokenKey));
  const [user, setUser] = useState(null); // {id, username, email, full_name, role, ...}
  const [loading, setLoading] = useState(true);
  const { instance, accounts } = useMsal(); // Get MSAL instance and accounts
  const isMsalAuthenticated = useIsAuthenticated(); // MSAL auth status

  // --- State for pending Microsoft registration ---
  const [isPendingMicrosoftRegistration, setIsPendingMicrosoftRegistration] =
    useState(false);
  const [pendingMicrosoftUserDetails, setPendingMicrosoftUserDetails] =
    useState(null);
  const [navigatorRole, setNavigatorRole] = useState(null); // Add state for navigator role
  // ----------------------------------------------

  // Set API base URL
  const API_URL = process.env.REACT_APP_API_URL || "https://isco-backend-deployment-932661235642.us-central1.run.app";

  // Define logout function first since it's used by other functions
  const logout = useCallback(() => {
    console.log("Logging out...");
    localStorage.removeItem(tokenKey);
    setToken(null);
    setUser(null);
    setIsPendingMicrosoftRegistration(false); // Clear pending state
    setPendingMicrosoftUserDetails(null);
    delete axios.defaults.headers.common["Authorization"];
    // Try popup logout, fallback to redirect if popup fails
    instance.logoutPopup({ postLogoutRedirectUri: "/login" }).catch((e) => {
      console.error("MSAL popup logout failed, trying redirect: ", e);
      instance.logoutRedirect({ postLogoutRedirectUri: "/login" }).catch((redirectError) => {
        console.error("MSAL redirect logout also failed: ", redirectError);
      });
    });
    console.log("Logout complete.");
  }, [instance]);

  // Set up axios interceptors for authentication
  useEffect(() => {
    axios.defaults.baseURL = API_URL;

    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const localToken = localStorage.getItem(tokenKey);
        if (localToken && !config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${localToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (
          error.response &&
          error.response.status === 401 &&
          !error.config.url.includes("/auth/microsoft/callback") && // Don't auto-logout during initial MS callback check
          !error.config.url.includes("/auth/microsoft/complete-registration") // Don't auto-logout during registration completion
        ) {
          console.log("Axios Interceptor: Received 401, logging out.");
          logout(); // Call unified logout
        }
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []); // Remove API_URL dependency

  // Fetch current user data on mount or token change
  useEffect(() => {
    const currentAppToken = localStorage.getItem(tokenKey);
    if (currentAppToken) {
      console.log("App token found, validating...");
      console.log("Token length:", currentAppToken.length);
      console.log(
        "Token starts with:",
        currentAppToken.substring(0, 20) + "...",
      );

      // Check if token is a valid JWT format (header.payload.signature)
      const tokenParts = currentAppToken.split(".");
      if (tokenParts.length !== 3) {
        console.error("Token does not appear to be in valid JWT format");
      }

      axios.defaults.headers.common["Authorization"] =
        `Bearer ${currentAppToken}`;
      console.log(
        "Authorization header set:",
        `Bearer ${currentAppToken.substring(0, 20)}...`,
      );
      axios
        .get("/auth/current-user")
        .then((res) => {
          console.log("App token validated, user set:", res.data.username);
          console.log("Full user data:", res.data);
          setUser(res.data);
          setToken(currentAppToken); // Ensure local state is synced
          setIsPendingMicrosoftRegistration(false); // Ensure pending state is cleared if logged in
          setPendingMicrosoftUserDetails(null);
        })
        .catch((err) => {
          console.error("App token validation failed:", err.response?.status);
          console.error("Error details:", {
            data: err.response?.data,
            headers: err.response?.headers,
            config: {
              url: err.config?.url,
              method: err.config?.method,
              baseURL: err.config?.baseURL,
              // Only show first 30 chars of auth header to avoid leaking full token in logs
              headers: {
                ...err.config?.headers,
                Authorization: err.config?.headers?.Authorization
                  ? `${err.config.headers.Authorization.substring(0, 30)}...`
                  : undefined,
              },
            },
          });

          // Don't logout on 401 here, let the interceptor handle it if needed
          // Avoids logout loops if /current-user itself returns 401
          if (err.response?.status !== 401) {
            logout(); // Logout only if it's not a 401 (interceptor handles 401)
          }
        })
        .finally(() => setLoading(false));
    } else {
      console.log("No app token found.");
      delete axios.defaults.headers.common["Authorization"];
      setUser(null);
      setToken(null);
      setLoading(false);
    }
  }, []); // Run only on mount initially

  // Function to handle traditional login AND completion of MS registration
  const login = useCallback(async ({ token: appToken }) => {
    console.log("Login function called, storing app token.");
    console.log("App token exists:", !!appToken);
    localStorage.setItem(tokenKey, appToken);
    setToken(appToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${appToken}`;
    setIsPendingMicrosoftRegistration(false); // Clear pending state
    setPendingMicrosoftUserDetails(null);

    try {
      console.log("Making request to /auth/current-user after login...");
      const res = await axios.get("/auth/current-user");
      console.log("User data fetched successfully after login:", res.data.username);
      console.log("Full user data after login:", res.data);
      setUser(res.data);
      return res.data;
    } catch (error) {
      console.error("Failed to fetch user data after login:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      // If fetching user fails, don't logout - backend might be temporarily down
      console.warn("Backend verification failed, but token exists. User may still be authenticated.");
      throw new Error("Backend service unavailable. Please try again later.");
    }
  }, [logout]); // Add logout to dependencies

  const getUser = async (accessToken, userId) => {
    const response = await fetch(
      `https://graph.microsoft.com/v1.0/users/${userId}?$select=id,displayName,userPrincipalName,onPremisesSamAccountName`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );
    
    return response.json();
  };

  // Add a new function to get Microsoft user profile
  const getMicrosoftUserProfile = useCallback(async (providedAccount) => {
    try {
      console.log("Getting Microsoft user profile");
      console.log("Provided account:", providedAccount);
      console.log("MSAL accounts:", accounts);
      console.log("Active account:", instance.getActiveAccount());

      // Use provided account first, then fall back to active account or first account
      const account =
        providedAccount ||
        instance.getActiveAccount() ||
        (accounts.length > 0 ? accounts[0] : null);

      if (!account) {
        console.log("No MSAL account available for Graph API call");
        return null;
      }

      let tokenResponse;
      try {
        // First try to acquire token silently with Directory.Read.All
        console.log("Attempting silent token acquisition with Directory.Read.All");
        tokenResponse = await instance.acquireTokenSilent({
          scopes: ["User.Read", "Directory.Read.All"],
          account: account,
          forceRefresh: true // Force token refresh
        });
      } catch (silentError) {
        console.log("Silent token acquisition failed:", silentError.name);
        
        if (silentError.name === "InteractionRequiredAuthError") {
          console.log("Interaction required for Directory.Read.All, attempting popup");
          try {
            // Fall back to interactive popup for elevated permissions
            tokenResponse = await instance.acquireTokenPopup({
              scopes: ["User.Read", "Directory.Read.All"],
              account: account
            });
            console.log("Interactive token acquisition succeeded");
          } catch (popupError) {
            console.error("Interactive popup failed for Directory.Read.All:", popupError);
            
            // Try one more time with reduced scopes if elevated scopes fail
            console.log("Falling back to basic User.Read scope only");
            try {
              tokenResponse = await instance.acquireTokenSilent({
                scopes: ["User.Read"],
                account: account,
              });
            } catch (basicScopeError) {
              console.error("Even basic scope failed:", basicScopeError);
              throw basicScopeError;
            }
          }
        } else {
          // For other errors, re-throw
          throw silentError;
        }
      }

      console.log("Graph API token acquired:", !!tokenResponse.accessToken);

      // Call Graph API with access token
      // Only include onPremisesSamAccountName if we got elevated permissions
      const requestUrl = tokenResponse.scopes.includes("Directory.Read.All") 
        ? "https://graph.microsoft.com/v1.0/me?$select=id,displayName,onPremisesSamAccountName"
        : "https://graph.microsoft.com/v1.0/me?$select=id,displayName";
      
      console.log("Requesting from Graph API with URL:", requestUrl);
      const response = await fetch(
        requestUrl,
        {
          headers: {
            Authorization: `Bearer ${tokenResponse.accessToken}`,
          },
        },
      );

      // Check for response errors
      if (!response.ok) {
        console.error("Graph API request failed:", response.status, response.statusText);
        try {
          const errorData = await response.json();
          console.error("Graph API error details:", errorData);
        } catch (e) {
          console.error("Could not parse error response:", e);
        }
        return null;
      }

      const userData = await response.json();
      console.log("Graph API response:", userData);
      
      // Handle onPremisesSamAccountName gracefully
      if (userData.onPremisesSamAccountName) {
        console.log("SAM Account Name from Graph API:", userData.onPremisesSamAccountName);
        
        // Send SAM account name to backend
        try {
          const response = await axios.post("/employee/get_navigator_permissions", {
            user_id: userData.onPremisesSamAccountName
          });
          console.log("User ID sent to backend");
          
          // Store the navigator role from the response
          console.log("Navigator permissions full response:", response.data);
          
          if (response.data && response.data.org_role) {
            console.log("Navigator role received:", response.data.org_role);
            setNavigatorRole(response.data.org_role);
          } else {
            console.log("No org_role in response:", response.data);
            setNavigatorRole(response.data?.org_role || null);
          }
        } catch (error) {
          // Log the error but don't throw - allow registration to continue
          console.error("Error sending user ID to backend:", error);
          console.log("Continuing with registration despite navigator service error");
          setNavigatorRole(null);
          
          // Don't rethrow the error - we want to continue the flow
        }
      } else {
        // Add a mock/empty value to maintain API compatibility
        userData.onPremisesSamAccountName = "";
        console.log("SAM Account Name not available (may need Directory.Read.All permission)");
        setNavigatorRole(null);
      }
      
      return userData;
    } catch (error) {
      console.error("Error calling Graph API:", error);
      return null;
    }
  }, [instance, accounts]);

  // Function to handle the callback after successful Microsoft login
  const handleMicrosoftLoginCallback = useCallback(
    async (microsoftIdToken, msalAccount) => {
      console.log(
        "handleMicrosoftLoginCallback: Sending MS ID token to backend...",
      );
      setLoading(true); // Indicate loading during backend check
      setIsPendingMicrosoftRegistration(false); // Reset pending state
      setPendingMicrosoftUserDetails(null);
      
      try {
        // Call Graph API to get user profile including onPremisesSamAccountName
        // This must happen before backend registration to get the role
        console.log("Getting Microsoft profile before proceeding with backend callback");
        const userProfile = await getMicrosoftUserProfile(msalAccount);
        console.log("Microsoft profile received:", userProfile);
        
        // Now proceed with the backend callback
        const response = await axios.post("/auth/microsoft/callback", {
          id_token: microsoftIdToken,
        });

        console.log("Backend /auth/microsoft/callback response:", response.data);
        console.log("ms_user_details exists:", !!response.data.ms_user_details);        
        // if (response.data.ms_user_details) {
        //   console.log("SAM Account Name:", response.data.ms_user_details.onPremisesSamAccountName);
        // }

        if (response.data.status === "authenticated") {
          const appToken = response.data.access_token;
          const idToken = response.data;
          console.log("Backend returned app token:", appToken ? "Yes" : "No");
          console.log("Backend returned app token:", appToken);
          console.log("Backend returned access info:", idToken);
          if (appToken) {
            await login({ token: appToken });
            console.log("Existing Microsoft user logged in.");
            // Navigation happens in the component calling this
          } else {
            throw new Error("Backend did not return an application token.");
          }
        } else if (response.data.status === "registration_required") {
          console.log("Backend indicated registration required.");
          setPendingMicrosoftUserDetails(response.data.ms_user_details);
          setIsPendingMicrosoftRegistration(true);
          // Do NOT call login() here. Navigation to registration page happens in component.
        } else {
          throw new Error(
            `Unexpected status from backend: ${response.data.status}`,
          );
        }
      } catch (error) {
        console.error(
          "Error during Microsoft login callback to backend:",
          error.response?.data || error.message,
        );
        logout(); // Logout fully on error
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [login, instance, getMicrosoftUserProfile], // Include dependencies
  );

  // Add useEffect to handle axios interceptor cleanup with logout dependency
  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
        (response) => response,
        (error) => {
          if (
            error.response &&
            error.response.status === 401 &&
            !error.config.url.includes("/auth/microsoft/callback") &&
            !error.config.url.includes("/auth/microsoft/complete-registration")
          ) {
            console.log("Axios Interceptor (in useEffect): Received 401, logging out.");
            logout();
          }
          return Promise.reject(error);
        },
      );
      return () => {
          axios.interceptors.response.eject(responseInterceptor);
      };
  }, [logout]);

  function updateProfile(profile) {
    return axios.put("/auth/current-user", profile).then((res) => {
      setUser(res.data);
      return res.data;
    });
  }

  // Determine overall authentication status
  const authed = !!token && !!user;
  const authStatus = isPendingMicrosoftRegistration
    ? "pending_registration"
    : authed
      ? "authenticated"
      : "unauthenticated";

  console.log("AuthContext State:", {
    authStatus,
    loading,
    user: user?.username,
    isPendingMicrosoftRegistration,
    pendingMicrosoftUserDetails: !!pendingMicrosoftUserDetails,
  });

  // Add useEffect to call getMicrosoftUserProfile when user becomes authenticated
  useEffect(() => {
    console.log("Authentication state check: ", {
      user: !!user,
      username: user?.username,
      isMsalAuthenticated,
      msalAccounts: accounts.length,
      activeAccount: !!instance.getActiveAccount(),
    });

    if (user) {
      console.log("User is authenticated with backend");
      if (isMsalAuthenticated) {
        console.log(
          "User is also authenticated with MSAL, getting Microsoft profile",
        );
        getMicrosoftUserProfile();
      } else {
        console.log("User authenticated with backend but not with MSAL yet");
        // Try to login with MSAL if not authenticated
        if (accounts.length === 0) {
          console.log(
            "No MSAL accounts available, might need to login with Microsoft first",
          );
        }
      }
    }
  }, [user, isMsalAuthenticated, getMicrosoftUserProfile, accounts, instance]);

  // Add a manual trigger function for getting profile
  const triggerGetMicrosoftProfile = useCallback(async () => {
    console.log("Manually triggering Microsoft profile fetch");
    try {
      // Try to login if not already authenticated
      if (!isMsalAuthenticated && accounts.length === 0) {
        console.log("Attempting Microsoft login");
        // Add Directory.Read.All to the login request to ensure proper consent
        const loginParams = {
          ...loginRequest,
          scopes: [...loginRequest.scopes, "Directory.Read.All"]
        };
        await instance.loginPopup(loginParams);
      }
      return await getMicrosoftUserProfile();
    } catch (error) {
      console.error("Error in manual Microsoft profile fetch:", error);
      return null;
    }
  }, [isMsalAuthenticated, accounts, instance, getMicrosoftUserProfile]);

  return (
    <AuthContext.Provider
      value={{
        authStatus, // Use this instead of just 'authed'
        user,
        token,
        loading,
        login, // Expose login for completing registration
        logout, // Expose unified logout
        updateProfile,
        setUser, // Keep if needed externally
        handleMicrosoftLoginCallback, // Expose MSAL callback handler
        isPendingMicrosoftRegistration, // Expose pending state
        pendingMicrosoftUserDetails, // Expose details for registration page
        clearPendingRegistration: () => {
          // Function to clear pending state if user navigates away
          setIsPendingMicrosoftRegistration(false);
          setPendingMicrosoftUserDetails(null);
        },
        getMicrosoftUserProfile, // Expose the new function
        triggerGetMicrosoftProfile, // Expose manual trigger function
        navigatorRole, // Expose navigator role
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}